import {
  getStudentCities,
  getStudentDurations,
  getStudentRooms,
  getStudentStructures
} from './booking-student.api'
import {
  getWorkerCities,
  getWorkerDurations,
  getWorkerRooms,
  getWorkerStructures
} from './booking-worker.api'
import {
  getAreeDisciplinari,
  getAnnoCorso,
  getAvailableRooms,
  getCountries,
  getFasceIsee,
  getUniversita
} from './codes.api'

type LookupItem = Record<string, unknown>

interface LookupResolverParams {
  endpoint: string
  lang: string
  parentValue?: string
}

const lookupResponseCache = new Map<string, LookupItem[]>()
const lookupInFlightCache = new Map<string, Promise<LookupItem[]>>()

interface AvailabilityParams {
  journey: 'student' | 'worker'
  sede: string
  checkin: string
  durata: string
  gender: string
  tipologia: string
  lang: string
}

const STUDENT_LOOKUP_PREFIX = '/api/booking-student/lookups'
const WORKER_LOOKUP_PREFIX = '/api/booking-worker/lookups'

function ensureLookupArray(input: unknown): LookupItem[] {
  if (Array.isArray(input)) {
    return input as LookupItem[]
  }

  if (!input || typeof input !== 'object') {
    return []
  }

  const record = input as Record<string, unknown>
  const candidates = [record.body, record.data, record.items, record.results]
  const nestedArray = candidates.find((candidate) => Array.isArray(candidate))

  return Array.isArray(nestedArray) ? (nestedArray as LookupItem[]) : []
}

function toSlugName(items: unknown) {
  return ensureLookupArray(items).map((item, index) => {
    const value =
      String(
        item.value ?? item.slug ?? item.id ?? item.codice ?? item.code ?? item.key ?? ''
      ).trim() || `option-${index}`
    const label = String(
      item.label ??
        item.name ??
        item.nome ??
        item.descrizione ??
        item.description ??
        item.desc ??
        item.durata ??
        item.testo ??
        value
    )

    return {
      ...item,
      value,
      label,
      slug: value,
      name: label
    }
  })
}

function toIdNome(items: unknown) {
  return ensureLookupArray(items).map((item, index) => {
    const value =
      String(item.value ?? item.id ?? item.slug ?? item.codice ?? item.code ?? '').trim() ||
      `option-${index}`
    const label = String(item.label ?? item.nome ?? item.name ?? value)

    return {
      ...item,
      value,
      label,
      id: value,
      nome: label
    }
  })
}

export async function resolveLookupOptions({
  endpoint,
  lang,
  parentValue
}: LookupResolverParams): Promise<LookupItem[]> {
  const normalizedEndpoint = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint
  const cacheKey = `${normalizedEndpoint}|${lang}|${parentValue ?? ''}`

  const cached = lookupResponseCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const inFlight = lookupInFlightCache.get(cacheKey)
  if (inFlight) {
    return inFlight
  }

  const request = (async (): Promise<LookupItem[]> => {
    if (normalizedEndpoint === `${STUDENT_LOOKUP_PREFIX}/cities`) {
      return toSlugName(await getStudentCities(lang))
    }

    if (normalizedEndpoint === `${WORKER_LOOKUP_PREFIX}/cities`) {
      return toSlugName(await getWorkerCities(lang))
    }

    if (normalizedEndpoint === `${STUDENT_LOOKUP_PREFIX}/structures`) {
      if (!parentValue) return []
      return toSlugName(await getStudentStructures(parentValue, lang))
    }

    if (normalizedEndpoint === `${WORKER_LOOKUP_PREFIX}/structures`) {
      if (!parentValue) return []
      return toSlugName(await getWorkerStructures(parentValue, lang))
    }

    if (normalizedEndpoint === `${STUDENT_LOOKUP_PREFIX}/rooms`) {
      if (!parentValue) return []
      return toSlugName(await getStudentRooms(parentValue, lang))
    }

    if (normalizedEndpoint === `${WORKER_LOOKUP_PREFIX}/rooms`) {
      if (!parentValue) return []
      return toSlugName(await getWorkerRooms(parentValue, lang))
    }

    if (normalizedEndpoint === `${STUDENT_LOOKUP_PREFIX}/durations`) {
      return toSlugName(await getStudentDurations(lang))
    }

    if (normalizedEndpoint === `${WORKER_LOOKUP_PREFIX}/durations`) {
      return toSlugName(await getWorkerDurations(lang))
    }

    if (normalizedEndpoint === '/api/lookups/universities') {
      return toIdNome(await getUniversita(lang))
    }

    if (normalizedEndpoint === '/api/lookups/faculties') {
      return toIdNome(await getAreeDisciplinari(lang))
    }

    if (normalizedEndpoint === '/api/lookups/annocorso') {
      return toSlugName(await getAnnoCorso(lang))
    }

    if (normalizedEndpoint === '/api/lookups/countries') {
      return toSlugName(await getCountries(lang))
    }

    if (normalizedEndpoint === '/api/lookups/isee-ranges') {
      return toSlugName(await getFasceIsee(lang))
    }

    if (normalizedEndpoint.startsWith('/api/')) {
      return []
    }

    const url = new URL(endpoint, window.location.origin)
    if (parentValue) {
      url.searchParams.set('parent', parentValue)
    }

    const response = await fetch(url.toString())
    if (!response.ok) {
      throw new Error(`Lookup fetch failed (${response.status})`)
    }

    return (await response.json()) as LookupItem[]
  })()

  lookupInFlightCache.set(cacheKey, request)

  try {
    const result = await request
    lookupResponseCache.set(cacheKey, result)
    return result
  } finally {
    lookupInFlightCache.delete(cacheKey)
  }
}

export async function isStructureExhausted({
  journey: _journey,
  sede,
  checkin,
  durata,
  gender,
  tipologia,
  lang
}: AvailabilityParams): Promise<boolean> {
  void _journey

  const rooms = await getAvailableRooms(sede, checkin, durata, {
    gender,
    lang,
    tipologia
  })

  const first = rooms[0] as { rooms?: { esaurite?: boolean } } | undefined
  return Boolean(first?.rooms?.esaurite)
}
